import { configureStore } from '@reduxjs/toolkit'
import { Action } from 'redux'
import { ThunkDispatch, combineReducers } from '@reduxjs/toolkit'
import type { AxiosInstance } from 'axios'
import { persistReducer, persistStore, PersistConfig } from 'redux-persist'
import { createPersistStorage } from './storage'

// ** Reducers
import { httpClient, gygInternalApiClient, gygExternalApiClient } from '@/src/@core/lib/httpClient'
import chat from 'src/store/apps/chat'
import user from 'src/store/apps/user'
import email from 'src/store/apps/email'
import invoice from 'src/store/apps/invoice'
import permissions from 'src/store/apps/permissions'
import gygUser from 'src/store/apps/gygUser'
import gygUserIdentity from 'src/store/apps/gygUserIdentity'
import gygLoyaltyHistory from 'src/store/apps/gygLoyaltyHistory'
import gygCouponHistory from 'src/store/apps/gygCouponHistory'
import gygOrder from 'src/store/apps/gygOrder'
import gygLoyalty from './apps/gygLoyalty'
import { paymentSlice } from './apps/gygPayment'
import gygStoresList from './apps/gygStoresList'
import gygStoresTagsListed from './apps/gygStoreTags'
import gygStoreGroup from './apps/gygGroups'
import gyg365RestoList from './apps/gyg365Resto'
import gyg365RestoDisabledIngredients from './apps/resto365DisabledIngredients'
import gygSectionList from './apps/gygSections'
import gygStoreSectionTimeSlots from './apps/gygStoreSectionTimeSlots'
import gygUserDevice from './apps/gygUserDevice'
import gyg365FranchiseList from './apps/gyg365Franchise'
import priceTier from './apps/gygStorePriceTier'
import resto365UserManagement from './apps/resto365UserManagement'
import resto365AuditLogs from './apps/resto365AuditLogs'
import resto365FaqsData from './apps/resto365Faq'
import Equipment from './apps/gygEquipment'
import resto365Notifications from './apps/resto365Notifications'
import gygCalendar from './apps/gygCalendar'
import gygMenuContainers from './apps/gygMenuContainer'
import gygMenuContainerProduct from './apps/gygMenuContainerProduct'
import gygMenuTag from './apps/gygMenuTag'
import gygPosMenuConfiguration from './apps/gygPosMenuConfiguration'
import gygCategory from './apps/gygCategory'
import gygPosMenu from './apps/gygPosMenu'
import gygChannel from './apps/gygChannel'
import gygMenuTemplate from './apps/gygMenuTemplate'
import gygGroup from './apps/gygGroup'
import gygOperations from './apps/operations'
import gygTransformationTemplate from './apps/gygTransformationTemplate'
import gygMasterProduct from './apps/gygMasterProduct'
import gygEon from './apps/gygEon'
import gygEonActions from './apps/gygEonActions'
import gygEonTargets from './apps/gygEonTargets'
import gygIngredients from './apps/gygIngredient'
import gygRecipes from './apps/gygRecipes'
import gygRecipeGuideEquipments from './apps/gygRecipeGuideEquipments'
import gygFoodSafetyTags from './apps/gygFoodSafetyTags'
import gygRecipeGuideIngredients from './apps/gygRecipeGuideIngredients'
import gygEonFacts from './apps/gygEonFacts'
import gygEonMasterActions from './apps/gygEonMasterActions'
import gygEonComments from './apps/gygEonComments'
import resto365rtrForms from './apps/resto365ReadyToRoll'
import resto365Rss from './apps/resto365Rss'
import restaurantReducer from './slices/restaurantSlice'
import recipeEquipmentReducer from './apps/gygRecipeEquipment'
import gygIngredientsNutritionalInfo from './apps/gygIngredientsNutritionalInfo'
import gygIngredientsUsedQuantity from './apps/gygIngredientsUsedQuantity'
import incompleteMenuJobs from './apps/incompleteMenuJobs'
import delegation from './apps/delegation'
import storeSurchargeImport from './apps/storeSurchargeImport'
import menuEquipments from './apps/menuEquipments'

export type LoadingState = 'initial' | 'pending' | 'success' | 'error' | 'invalidated'

export interface AxiosMiddlewareExtras {
  httpClient: AxiosInstance
  gygInternalApiClient: AxiosInstance
  gygExternalApiClient: AxiosInstance
}

export type Redux = {
  getState: () => any
  dispatch: ThunkDispatch<any, AxiosMiddlewareExtras, Action<string>>
  extra: AxiosMiddlewareExtras
}

// Define the root state type
export type RootState = ReturnType<typeof rootReducer> & {
  _persist?: {
    version: number
    rehydrated: boolean
  }
}

const rootReducer = combineReducers({
  user,
  chat,
  email,
  invoice,
  permissions,
  gygUser,
  gygUserIdentity,
  gygLoyaltyHistory,
  gygCouponHistory,
  gygOrder,
  gygLoyalty,
  gygPayment: paymentSlice.reducer,
  gygStoresList,
  gygStoresTagsListed,
  gygStoreGroup,
  gyg365RestoList,
  gyg365RestoDisabledIngredients,
  gygSectionList,
  gygStoreSectionTimeSlots,
  gygUserDevice,
  gyg365FranchiseList,
  resto365UserManagement,
  resto365AuditLogs,
  priceTier,
  resto365FaqsData,
  Equipment,
  resto365Notifications,
  gygCalendar,
  gygMenuContainers,
  gygMenuContainerProduct,
  gygMenuTag,
  gygPosMenuConfiguration,
  gygCategory,
  resto365rtrForms,
  gygChannel,
  gygMenuTemplate,
  gygGroup,
  gygOperations,
  gygTransformationTemplate,
  resto365Rss,
  gygMasterProduct,
  gygPosMenu,
  restaurant: restaurantReducer,
  gygEon,
  gygEonActions,
  gygEonTargets,
  gygEonMasterActions,
  gygEonComments,
  gygRecipeEquipment: recipeEquipmentReducer,
  gygIngredients,
  gygRecipes,
  gygRecipeGuideEquipments,
  gygFoodSafetyTags,
  gygRecipeGuideIngredients,
  payment: paymentSlice.reducer,
  gygEonFacts,
  gygIngredientsNutritionalInfo,
  gygIngredientsUsedQuantity,
  incompleteMenuJobs,
  delegation,
  storeSurchargeImport,
  menuEquipments
})

const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  whitelist: [
    'user',
    'gygUser',
    'gygUserIdentity',
    'gygStoresList',
    'gygStoreGroup',
    'gyg365RestoList',
    'gyg365FranchiseList',
    'gyg365RestoDisabledIngredients',
    'gygUserDevice',
    'priceTier',
    'Equipment',
    'gygCalendar'
  ],
  storage: createPersistStorage(),
  serialize: true,
  timeout: 0, // Disable timeout for better SSR compatibility
  debug: process.env.NODE_ENV === 'development'
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/FLUSH'],
        ignoredPaths: ['_persist']
      },
      immutableCheck: {
        // Increase threshold for development performance
        warnAfter: 128
      },
      thunk: {
        extraArgument: {
          httpClient,
          gygInternalApiClient,
          gygExternalApiClient
        }
      }
    })
})

export type AppDispatch = typeof store.dispatch

// Create persistor with error handling
export const persistor = persistStore(store, null, () => {
  // Callback when rehydration is complete
  // Rehydration completed successfully
})
